import { loginAdmin, logoutSupabase, getUserProfile } from '../helper/supabaseClient';

// Environment validation
const API_URL = process.env.REACT_APP_API_URL;
if (!API_URL && process.env.NODE_ENV === 'production') {
  console.error('REACT_APP_API_URL is required in production');
}

// Development-only logging helper
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (isDev) console.log(...args); };

/**
 * Decode JWT token and extract claims
 * @param {string} token - JWT access token
 * @returns {object|null} - Decoded payload or null if invalid
 */
function decodeJwt(token) {
    try {
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
}

/**
 * Get JWT claims from the current access token
 * @returns {object} - Claims object with app_role and group_id
 */
function getJwtClaims() {
    const token = localStorage.getItem('accessToken');
    const decoded = decodeJwt(token);
    return {
        app_role: decoded?.app_role || null,
        group_id: decoded?.group_id || null,
        sub: decoded?.sub || null,
        exp: decoded?.exp || null
    };
}

export const authService = {
    async login(email, password) {
        try {
            devLog('authService.login called');
            // Try Supabase login
            const result = await loginAdmin(email, password);
            devLog('loginAdmin result:', result.success ? 'success' : 'failed');

            if (result.success) {
                // Get user profile from profiles table
                const { data: profile, error: profileError } = await getUserProfile(result.user.id);

                if (profileError) {
                    console.error('Error fetching user profile:', profileError);
                    // Continue even if profile fetch fails - profile might not exist yet
                }

                // Store session token and profile info
                if (result.session?.access_token) {
                    localStorage.setItem('accessToken', result.session.access_token);
                    localStorage.setItem('userId', result.user.id);

                    // Store user role and profile data
                    if (profile) {
                        localStorage.setItem('userRole', profile.role);
                        localStorage.setItem('userName', profile.name);
                        localStorage.setItem('userBalance', profile.balance);
                        devLog('Stored user profile');
                    }

                    devLog('Stored access token');
                }
                return {
                    success: true,
                    user: result.user,
                    profile: profile
                };
            } else {
                return {
                    success: false,
                    error: result.error || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login catch error:', error);
            return {
                success: false,
                error: error.message || 'Login failed'
            };
        }
    },

    // Register a new user via secure backend endpoint
    async register(email, password, phone = '') {
        try {
            devLog('authService.register called');
            
            const response = await fetch(`${API_URL}/api/users/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    password,
                    phone,
                    metadata: { registered_at: new Date().toISOString() }
                })
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                return {
                    success: false,
                    error: data.error || 'Registration failed'
                };
            }
            
            // Automatically login after registration
            return await this.login(email, password);
        } catch (error) {
            console.error('Registration error:', error);
            return { 
                success: false, 
                error: error.message || 'Registration failed' 
            };
        }
    },

    async logout() {
        try {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('userId');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('userBalance');

            // Logout from Supabase
            await logoutSupabase();

            return { success: true };
        } catch (error) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('userId');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('userBalance');
            return { success: false, error: error.message };
        }
    },

    getToken() {
        return localStorage.getItem('accessToken');
    },

    getUserId() {
        return localStorage.getItem('userId');
    },

    /**
     * Get user role - prefers JWT claim, falls back to localStorage
     * JWT claims are more secure as they are signed by Supabase
     */
    getUserRole() {
        const claims = getJwtClaims();
        // Prefer JWT claim, fallback to localStorage for backwards compatibility
        return claims.app_role || localStorage.getItem('userRole');
    },

    /**
     * Get user's group ID from JWT claims
     */
    getUserGroupId() {
        const claims = getJwtClaims();
        return claims.group_id;
    },

    getUserName() {
        return localStorage.getItem('userName');
    },

    getUserBalance() {
        return parseFloat(localStorage.getItem('userBalance')) || 0;
    },

    isAuthenticated() {
        const token = localStorage.getItem('accessToken');
        if (!token) return false;

        // Check if token is expired
        const claims = getJwtClaims();
        if (claims.exp) {
            const now = Math.floor(Date.now() / 1000);
            if (claims.exp < now) {
                // Token expired, clear storage
                this.logout();
                return false;
            }
        }
        return true;
    },

    isAdmin() {
        return this.getUserRole() === 'admin';
    },

    isManager() {
        return this.getUserRole() === 'manager';
    },

    isSuperManager() {
        return this.getUserRole() === 'super_manager';
    },

    isDealer() {
        return this.getUserRole() === 'dealer';
    },

    isManagerOrAbove() {
        const role = this.getUserRole();
        return ['admin', 'super_manager', 'manager'].includes(role);
    },

    isSuperManagerOrAbove() {
        const role = this.getUserRole();
        return ['admin', 'super_manager'].includes(role);
    },

    canManageUsers() {
        const role = this.getUserRole();
        return role === 'admin' || role === 'super_manager' || role === 'manager';
    },

    /**
     * Get all JWT claims for debugging or advanced use
     */
    getJwtClaims() {
        return getJwtClaims();
    },

    /**
     * Check if user can manage a specific user based on group membership
     * @param {string} targetGroupId - The group ID of the target user
     * @returns {boolean}
     */
    canManageUserInGroup(targetGroupId) {
        const role = this.getUserRole();
        if (role === 'admin') return true;
        if (role === 'super_manager') return true; // Super manager can manage users in their assigned groups
        if (role === 'manager') {
            const myGroupId = this.getUserGroupId();
            return myGroupId && myGroupId === targetGroupId;
        }
        return false;
    },

    /**
     * Get current user object with id, role, group_id, name, balance
     * @returns {object|null} - User object or null if not authenticated
     */
    getUser() {
        if (!this.isAuthenticated()) return null;
        return {
            id: this.getUserId(),
            role: this.getUserRole(),
            group_id: this.getUserGroupId(),
            name: this.getUserName(),
            balance: this.getUserBalance()
        };
    }
};